import {
  InfoCard,
  InfoCardVariant,
} from '@monorepo/shared/componentsV2/InfoCard';
import { Anchor, Link } from '@monorepo/shared/componentsV2/Link';
import { edpIncompleteDataErrorProductGuideUrl } from '@monorepo/shared/externalLinks/productGuide';
import { useLinkOnSameOrg } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameOrg';
import { WorkflowResponse } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { useWorkflowsProducingIncompleteData } from '../../hooks/useWorkflowsProducingIncompleteData';
import { RouteEditMode, WizardStep, WizardStepUrlPart } from '../types';

const linkStyles = css`
  font-weight: 700;
  color: inherit;
  text-decoration: underline;
`;

const StyledLink = styled(Link)`
  ${linkStyles}
`;

const StyledAnchor = styled(Anchor)`
  ${linkStyles}
`;

const StyledInfoCard = styled(InfoCard)`
  max-width: 50rem;
`;

const StyledList = styled.ul`
  padding-left: 1rem;
  margin-top: 0;
`;

type WorkflowIncompleteDataMessagesProps = {
  className?: string;
  workflow: WorkflowResponse;
};

export function WorkflowIncompleteDataMessages({
  className,
  workflow,
}: WorkflowIncompleteDataMessagesProps) {
  const baseLink = useLinkOnSameOrg('/data-setup/workflows');
  const { upstreamWorkflowIdUsingIncompleteData } = workflow.outputDataset;
  const doesItUseIncompleteData =
    upstreamWorkflowIdUsingIncompleteData === workflow.id;

  const { workflowsProducingIncompleteData } =
    useWorkflowsProducingIncompleteData({
      organizationId: workflow.organizationId,
      workflowId: workflow.id,
      config: { enabled: doesItUseIncompleteData, useErrorBoundary: false },
    });

  const getWorkflowStepsLink = useCallback(
    (workflowId: string) =>
      `${baseLink}/${workflowId}/${RouteEditMode.EDIT}/${
        WizardStepUrlPart[WizardStep.STEPS]
      }`,
    [baseLink],
  );

  const orderedProducingWorkflows = useMemo(
    () =>
      (workflowsProducingIncompleteData ?? []).slice().sort((w1, w2) => {
        if (w1.id === workflow.id) return -1;
        if (w2.id === workflow.id) return 1;
        return 0;
      }),
    [workflow.id, workflowsProducingIncompleteData],
  );
  const firstProducingWorkflow = orderedProducingWorkflows[0];

  if (!upstreamWorkflowIdUsingIncompleteData) {
    return null;
  }

  if (!doesItUseIncompleteData) {
    return (
      <StyledInfoCard className={className} variant={InfoCardVariant.ERROR}>
        This Workflow contains a broken calculation. The source for this
        workflow uses a field that contains some ambiguous values and must be
        corrected.{' '}
        <StyledLink
          to={getWorkflowStepsLink(upstreamWorkflowIdUsingIncompleteData)}
        >
          View source
        </StyledLink>
      </StyledInfoCard>
    );
  }

  const { usedPropertiesWithIncompleteData } = workflow;
  const isPlural = usedPropertiesWithIncompleteData.length > 1;

  return (
    <StyledInfoCard className={className} variant={InfoCardVariant.ERROR}>
      One or more steps in this Workflow are using{' '}
      {usedPropertiesWithIncompleteData.map((property, index) => (
        <React.Fragment key={property.id}>
          {index > 0 && ', '}
          <b>{property.name}</b>
        </React.Fragment>
      ))}{' '}
      which {isPlural ? 'contain' : 'contains'} some ambiguous values.
      Calculations may be broken or inaccurate.{' '}
      <StyledAnchor
        href={edpIncompleteDataErrorProductGuideUrl}
        target="_blank"
      >
        How do I fix this?
      </StyledAnchor>
      {orderedProducingWorkflows && !!orderedProducingWorkflows.length && (
        <>
          <div style={{ marginTop: '0.5rem' }}>
            Workflows that produced {isPlural ? 'these fields' : 'this field'}:{' '}
            {orderedProducingWorkflows.length === 1 &&
              firstProducingWorkflow &&
              (firstProducingWorkflow.id === workflow.id ? (
                workflow.name
              ) : (
                <StyledLink
                  to={getWorkflowStepsLink(firstProducingWorkflow.id)}
                >
                  {firstProducingWorkflow.name}
                </StyledLink>
              ))}
          </div>
          {orderedProducingWorkflows.length > 1 && (
            <StyledList>
              {orderedProducingWorkflows.map((w) =>
                w.id === workflow.id ? (
                  <li>{workflow.name}</li>
                ) : (
                  <li key={w.id}>
                    <StyledLink to={getWorkflowStepsLink(w.id)}>
                      {w.name}
                    </StyledLink>
                  </li>
                ),
              )}
            </StyledList>
          )}
        </>
      )}
    </StyledInfoCard>
  );
}
